import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SectionTitle from "./SectionTitle";
import kitchenImg from "@/assets/kitchen-white-island.jpeg";
import bathImg from "@/assets/bathroom-vanity-1.jpg";
import laundryImg from "@/assets/laundry-green.jpeg";
import mudImg from "@/assets/mud-room.jpeg";


const categories = [
  { label: "Kitchens", alt: "Custom kitchen cabinetry", img: kitchenImg, href: "/kitchens", desc: "Heirloom-quality kitchen cabinetry built to last.", plate: "01" },
  { label: "Bathrooms", alt: "Custom bathroom vanities", img: bathImg, href: "/bathrooms", desc: "Custom vanities and bath storage solutions.", plate: "02" },
  { label: "Laundry Rooms", alt: "Custom laundry room cabinets", img: laundryImg, href: "/#gallery", desc: "Functional, beautiful laundry cabinetry.", plate: "03" },
  { label: "Mud Rooms", alt: "Custom mud room storage", img: mudImg, href: "/#gallery", desc: "Cubbies, hooks, and benches made to order.", plate: "04" },
];

const CategoriesStrip = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 1 | -1) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 280, behavior: "smooth" });
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex justify-center">
          <SectionTitle
            index="II"
            eyebrow="What We Build"
            accent="Custom Built"
            rest="Cabinetry"
            subtitle="Browse our categories of fine cabinetry."
          />
        </div>


        <div className="relative mt-12">
          <div
            ref={scrollRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: "none" }}
          >
            {categories.map((cat) => (
              <Link
                key={cat.label}
                to={cat.href}
                className="snap-start shrink-0 w-[220px] sm:w-[240px] group"
              >
                <div className="aspect-[4/5] overflow-hidden bg-secondary mb-4">
                  <img
                    src={cat.img}
                    alt={cat.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <h3
                  className="text-lg text-foreground group-hover:text-accent transition-colors"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {cat.label}
                </h3>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{cat.desc}</p>
              </Link>

            ))}
          </div>

          <div className="flex justify-center gap-3 mt-8">
            <button
              onClick={() => scroll(-1)}
              aria-label="Scroll left"
              className="w-10 h-10 border border-border flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => scroll(1)}
              aria-label="Scroll right"
              className="w-10 h-10 border border-border flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoriesStrip;
